import type { KeyboardEvent } from 'react';
import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import type { MessageKey } from '../types';

import {
  StyledChatBar,
  StyledChatInput,
  StyledChatInputWrapper,
  StyledPredefinedChip,
  StyledPredefinedChips,
  StyledSendButton,
} from './styles';

type PredefinedMessage = {
  key: MessageKey;
  labelKey: string;
  fallback: string;
};

const PREDEFINED_MESSAGES: PredefinedMessage[] = [
  {
    key: 'alternative_insights',
    labelKey: 'company.overview.ai-outreach.chip_alternative_insights',
    fallback: 'Present alternative insights',
  },
  {
    key: 'replace_competitors',
    labelKey: 'company.overview.ai-outreach.chip_replace_competitors',
    fallback: 'Replace competitors',
  },
  {
    key: 'make_shorter',
    labelKey: 'company.overview.ai-outreach.chip_make_shorter',
    fallback: 'Make it shorter',
  },
  {
    key: 'more_formal',
    labelKey: 'company.overview.ai-outreach.chip_more_formal',
    fallback: 'More formal tone',
  },
];

type AIOutreachChatBarProps = {
  onSendMessage: (key: MessageKey, message: string) => void;
  isDisabled: boolean;
  hidePredefinedChips?: boolean;
};

export const AIOutreachChatBar = ({
  onSendMessage,
  isDisabled,
  hidePredefinedChips = false,
}: AIOutreachChatBarProps) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');

  const handleSend = useCallback(() => {
    const trimmed = message.trim();
    if (!trimmed || isDisabled) {
      return;
    }
    onSendMessage('free_text', trimmed);
    setMessage('');
  }, [message, isDisabled, onSendMessage]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <>
      {hidePredefinedChips ? null : (
        <StyledPredefinedChips>
          {PREDEFINED_MESSAGES.map(({ key, labelKey, fallback }) => (
            <StyledPredefinedChip
              key={key}
              disabled={isDisabled}
              onClick={() => onSendMessage(key, t(labelKey, fallback))}
            >
              {t(labelKey, fallback)}
            </StyledPredefinedChip>
          ))}
        </StyledPredefinedChips>
      )}
      <StyledChatBar data-automation="AIOutreachChatBar">
        <StyledChatInputWrapper>
          <StyledChatInput
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={String(t('company.overview.ai-outreach.chat_placeholder', 'Change anything?'))}
            disabled={isDisabled}
            data-automation="AIOutreachChatInput"
          />
          <StyledSendButton
            onClick={handleSend}
            disabled={isDisabled || !message.trim()}
            hasText={!!message.trim()}
            data-automation="AIOutreachSendButton"
          >
            <SWReactIcons iconName="arrow-up" size={16} iconColor={colorsPalettes.carbon[0]} />
          </StyledSendButton>
        </StyledChatInputWrapper>
      </StyledChatBar>
    </>
  );
};
